/**
 * Leitura de planilha CSV, sem biblioteca.
 *
 * A importação de contatos dizia "Importação iniciada" e carregava sempre a
 * mesma planilha de exemplo: nenhum arquivo era lido. CSV é o formato que toda
 * planilha salva (Excel: Arquivo › Salvar como › CSV) e dá para ler aqui, com a
 * regra das aspas, sem 300 KB de leitor de XLSX no bundle.
 */

export interface Planilha {
    colunas: string[];
    /** Cada linha com o mesmo número de células que o cabeçalho. */
    linhas: string[][];
}

/** Marca de ordem de bytes que o Excel põe no início do arquivo. */
const BOM = String.fromCharCode(0xfeff);
/** O caractere que aparece no lugar da letra quando a codificação lida está errada. */
const SUBSTITUICAO = String.fromCharCode(0xfffd);

/** O separador que mais aparece no cabeçalho. Excel em pt-BR salva com `;`. */
export function detectarSeparador(cabecalho: string): string {
    const contar = (sep: string) => cabecalho.split(sep).length - 1;
    return [';', ',', '\t'].reduce((melhor, sep) => (contar(sep) > contar(melhor) ? sep : melhor), ';');
}

export function lerCsv(bruto: string): Planilha {
    const texto = bruto.startsWith(BOM) ? bruto.slice(1) : bruto;
    const separador = detectarSeparador(texto.split(/\r?\n/, 1)[0] ?? '');

    const registros: string[][] = [];
    let registro: string[] = [];
    let campo = '';
    let entreAspas = false;

    for (let i = 0; i < texto.length; i += 1) {
        const c = texto.charAt(i);
        if (entreAspas) {
            if (c === '"') {
                /* Aspas dobradas dentro de aspas são uma aspa literal. */
                if (texto.charAt(i + 1) === '"') { campo += '"'; i += 1; } else entreAspas = false;
            } else {
                campo += c;
            }
            continue;
        }
        if (c === '"' && campo.trim() === '') { entreAspas = true; campo = ''; continue; }
        if (c === separador) { registro.push(campo); campo = ''; continue; }
        if (c === '\n' || c === '\r') {
            if (c === '\r' && texto.charAt(i + 1) === '\n') i += 1;
            registro.push(campo);
            registros.push(registro);
            registro = [];
            campo = '';
            continue;
        }
        campo += c;
    }
    if (campo !== '' || registro.length > 0) {
        registro.push(campo);
        registros.push(registro);
    }

    const cheios = registros
        .map((r) => r.map((v) => v.trim()))
        .filter((r) => r.some((v) => v !== ''));
    const [cabecalho = [], ...linhas] = cheios;

    /* Duas colunas "Telefone" colidiriam no mapeamento, que usa o nome como
       chave: a segunda vira "Telefone (2)". */
    const vistas = new Map<string, number>();
    const colunas = cabecalho.map((nome, i) => {
        const base = nome || `Coluna ${i + 1}`;
        const vezes = (vistas.get(base) ?? 0) + 1;
        vistas.set(base, vezes);
        return vezes === 1 ? base : `${base} (${vezes})`;
    });

    return { colunas, linhas: linhas.map((l) => colunas.map((_, i) => l[i] ?? '')) };
}

/**
 * O texto do arquivo, na codificação certa.
 *
 * CSV salvo pelo Excel em pt-BR costuma vir em Windows-1252, e lido como UTF-8
 * todo "ã" vira o caractere de substituição. Se ele aparecer, lê de novo na
 * codificação do Windows.
 */
export function lerArquivoDeTexto(arquivo: Blob): Promise<string> {
    const ler = (codificacao: string) => new Promise<string>((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(typeof leitor.result === 'string' ? leitor.result : '');
        leitor.onerror = () => reject(leitor.error ?? new Error('Não foi possível ler o arquivo.'));
        leitor.readAsText(arquivo, codificacao);
    });
    return ler('utf-8').then((texto) => (texto.includes(SUBSTITUICAO) ? ler('windows-1252') : texto));
}
